import { useEffect, useMemo, useState, type JSX } from 'react';
import { pricingTierLabel } from '@tsr/shared-types';
import { fetchOperatorRateCard, updateOperatorRateCard, type RateCardView } from '../api';
import { Field, PortalSection, inputClass, primaryButtonClass } from './portalUi';

/**
 * Operator RATE CARD (drone marketplace pricing #344). The operator sets its OWN agency-level
 * rate — a mobilization/callout fee, an hourly rate, and an optional per-km transit rate (all
 * optional: a simple operator sets just an hourly). This is the competitive price surfaced at
 * MATCH TIME so a requestor sees an estimate BEFORE the operator hand-types a final quote.
 *
 * DISPLAY + ESTIMATE only — there are NO payment rails here. A government_owned operator is
 * $0-rated by definition (#343), so its card is READ-ONLY ("$0 · Government asset"); the server
 * also rejects a rate-card write from a government operator.
 */

/** A number → input string ('' for null). */
function toField(n: number | null): string {
  return n == null ? '' : String(n);
}

/** An input string → number | null (blank clears; a bad value is treated as blank). */
function fromField(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function RateCardSection(): JSX.Element {
  const [view, setView] = useState<RateCardView | null>(null);
  const [mobilization, setMobilization] = useState('');
  const [hourly, setHourly] = useState('');
  const [perKm, setPerKm] = useState('');
  const [saved, setSaved] = useState({ mobilization: '', hourly: '', perKm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function hydrate(v: RateCardView): void {
    setView(v);
    const m = toField(v.rate_card.mobilization_fee_cad);
    const h = toField(v.rate_card.hourly_rate_cad);
    const k = toField(v.rate_card.per_km_cad);
    setMobilization(m);
    setHourly(h);
    setPerKm(k);
    setSaved({ mobilization: m, hourly: h, perKm: k });
  }

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    void fetchOperatorRateCard(ac.signal)
      .then((v) => {
        if (alive && v) hydrate(v);
      })
      .catch(() => {});
    return () => {
      alive = false;
      ac.abort();
    };
  }, []);

  const isGov = view?.government_owned === true;
  const dirty = useMemo(
    () =>
      !isGov &&
      (mobilization !== saved.mobilization || hourly !== saved.hourly || perKm !== saved.perKm),
    [isGov, mobilization, hourly, perKm, saved],
  );

  async function save(): Promise<void> {
    if (!dirty || isGov) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateOperatorRateCard({
        mobilization_fee_cad: fromField(mobilization),
        hourly_rate_cad: fromField(hourly),
        per_km_cad: fromField(perKm),
      });
      hydrate(updated);
    } catch {
      setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PortalSection title="Pricing" testId="portal-rate-card">
      {isGov ? (
        <div className="text-sm text-slate-300" data-testid="rate-card-gov">
          <span className="font-medium text-slate-100">$0 · Government asset</span>
          <p className="mt-1 text-xs text-slate-500">
            Government / public-service fleets are no-charge in the marketplace. There is no rate
            card to set.
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            Set the rate the marketplace shows operators at match time. All optional — a simple
            operator sets just an hourly rate. Displayed as an estimate; you still confirm a final
            quote when you accept a mission.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Field label="Mobilization / call-out (CAD)">
                <input
                  data-testid="rate-mobilization-input"
                  className={inputClass}
                  type="number"
                  min={0}
                  step="0.01"
                  value={mobilization}
                  onChange={(e) => setMobilization(e.target.value)}
                  placeholder="e.g. 750"
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label="Hourly rate (CAD/hr)">
                <input
                  data-testid="rate-hourly-input"
                  className={inputClass}
                  type="number"
                  min={0}
                  step="0.01"
                  value={hourly}
                  onChange={(e) => setHourly(e.target.value)}
                  placeholder="e.g. 320"
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label="Per-km transit (CAD/km)">
                <input
                  data-testid="rate-perkm-input"
                  className={inputClass}
                  type="number"
                  min={0}
                  step="0.01"
                  value={perKm}
                  onChange={(e) => setPerKm(e.target.value)}
                  placeholder="e.g. 4.50"
                />
              </Field>
            </div>
            <button
              type="button"
              data-testid="rate-card-save"
              className={primaryButtonClass}
              disabled={!dirty || saving}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {view ? (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Marketplace tier</span>
              <span data-testid="rate-card-tier" className="text-slate-400">
                {pricingTierLabel(view.pricing_tier)}
              </span>
            </div>
          ) : null}
          {error ? <span className="text-xs text-amber-300">{error}</span> : null}
        </>
      )}
    </PortalSection>
  );
}
